import { Palette, RefreshCw, CheckCircle2, AlertCircle } from "lucide-react";
import type { ReportDesignerReview } from "@iamu/shared";
import { DesignerReviewSection } from "@/components/walkthrough/workspace/designer-review-section";
import { Kicker } from "@/components/ui/kicker";
import { Tonal } from "@/components/ui/tonal";
import { SeverityDot, severityTone } from "@/components/ui/severity-dot";
import type { LivePhase } from "@/hooks/use-walkthrough-stream";
import type { UnifiedLiveFinding } from "./types";

interface LensProgressItem {
  lensId: string;
  lensLabel: string;
  status: "analyzing" | "complete" | "failed";
  findingCount?: number;
  error?: string;
}

interface LiveWalkthroughDesignerProps {
  designerReview?: ReportDesignerReview | null;
  lensProgress?: LensProgressItem[];
  phase: LivePhase | null;
  isRunning: boolean;
  designerFindings: UnifiedLiveFinding[];
  onJumpToStep?: (stepNumber: number) => void;
}

export function LiveWalkthroughDesigner({
  designerReview,
  lensProgress = [],
  phase,
  isRunning,
  designerFindings,
  onJumpToStep,
}: LiveWalkthroughDesignerProps) {
  const isPostWalk = phase !== null && phase !== "walking";
  const designerLens = lensProgress.find((l) => l.lensId === "designer");

  return (
    <div className="space-y-8 px-7 py-9">
      <div className="flex items-baseline justify-between gap-3">
        <Kicker as="h2">Designer critique</Kicker>
        {designerReview?.label && (
          <span className="text-xs text-muted-foreground font-mono">
            {designerReview.label}
          </span>
        )}
      </div>

      {/* Render the full designer review if available */}
      {designerReview ? (
        <DesignerReviewSection review={designerReview} />
      ) : designerLens?.status === "analyzing" ? (
        <Tonal padding="md" className="space-y-3">
          <div className="flex items-center gap-3">
            <RefreshCw
              className="h-3.5 w-3.5 animate-spin text-primary"
              aria-hidden="true"
            />
            <Kicker>Designer pass active</Kicker>
          </div>
          <p className="text-base text-foreground">
            Auditing captured screens against project design conventions and
            rules.
          </p>
        </Tonal>
      ) : isRunning && !isPostWalk ? (
        <div className="py-10 text-center">
          <Palette
            className="mx-auto h-6 w-6 text-muted-foreground"
            aria-hidden="true"
          />
          <p className="mt-3 text-base text-foreground font-medium">
            Designer review begins after the walk
          </p>
          <p className="mt-1 text-sm text-muted-foreground max-w-sm mx-auto">
            The persona moves first to keep observations unpolluted. Once the
            journey finishes, the designer lens critiques captured screens
            against project design rules.
          </p>
        </div>
      ) : (
        <div className="py-8 text-center text-sm text-muted-foreground">
          No designer review available for this run.
        </div>
      )}

      {/* Analysis passes (lenses) */}
      {lensProgress.length > 0 && (
        <section className="space-y-4 border-t border-border/60 pt-6">
          <Kicker as="h3">Analysis passes</Kicker>
          <ul className="space-y-3">
            {lensProgress.map((l) => (
              <li
                key={l.lensId}
                className="flex items-baseline gap-3 text-base"
              >
                <span className="shrink-0 pt-[7px]">
                  {l.status === "analyzing" && (
                    <RefreshCw
                      className="h-3 w-3 animate-spin text-primary"
                      aria-hidden="true"
                    />
                  )}
                  {l.status === "complete" && (
                    <CheckCircle2
                      className="h-3.5 w-3.5 text-mark-helps"
                      aria-hidden="true"
                    />
                  )}
                  {l.status === "failed" && (
                    <AlertCircle
                      className="h-3.5 w-3.5 text-mark-critical"
                      aria-hidden="true"
                    />
                  )}
                </span>
                <span className="flex-1">
                  <span className="font-semibold">{l.lensLabel}</span>
                  <span className="ml-2 text-muted-foreground text-sm">
                    {l.status === "analyzing" && "Analysing…"}
                    {l.status === "complete" &&
                      l.findingCount !== undefined &&
                      `${l.findingCount} finding${l.findingCount !== 1 ? "s" : ""}`}
                    {l.status === "failed" && (l.error ?? "Failed")}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Designer findings list */}
      {designerFindings.length > 0 && (
        <section className="space-y-4 border-t border-border/60 pt-6">
          <Kicker as="h3">Designer findings ({designerFindings.length})</Kicker>
          <ul className="space-y-3">
            {designerFindings.map((f) => (
              <li
                key={f.id}
                className="rounded-md border border-border/60 bg-surface-tonal p-3 text-sm space-y-1"
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <SeverityDot tone={severityTone(f.severity)} />
                    <span className="font-medium text-foreground">
                      {f.title || f.description}
                    </span>
                  </div>
                  {f.stepNumber != null && (
                    <button
                      type="button"
                      onClick={() => onJumpToStep?.(f.stepNumber!)}
                      className="text-xs font-mono text-muted-foreground hover:text-foreground underline"
                    >
                      Step {f.stepNumber}
                    </button>
                  )}
                </div>
                {f.uiFact && (
                  <p className="text-muted-foreground text-xs">{f.uiFact}</p>
                )}
                {f.recommendation && (
                  <p className="text-muted-foreground text-xs">
                    {f.recommendation}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
